'use client'

import { useContext, useEffect, useState } from 'react'
import { throttle } from 'lodash'
import { HeaderContext } from 'ui/context/header'

/**
 * The header behaviours the Pages Router pages set up in effects, moved out of
 * the (now server-rendered) pages unchanged.
 */

/** Clear header while the page is scrolled to the top (harmony, nightvision, dry eye). */
export function ClearHeaderAtTop() {
  const headerContext = useContext(HeaderContext)

  useEffect(() => {
    const handleWindowScrollThrottled = throttle(() => {
      headerContext?.setIsClear(window.pageYOffset === 0)
    }, 100)
    window.addEventListener('scroll', handleWindowScrollThrottled)

    return () => {
      window.removeEventListener('scroll', handleWindowScrollThrottled)
    }
  }, [])

  return null
}

/**
 * Clear header at the top on desktop only (home, life with visian); `solid`
 * keeps it solid instead, re-applied on the same scroll and resize changes (US home).
 */
export function ClearHeaderAtTopOnDesktop({ solid = false }: { solid?: boolean }) {
  const headerContext = useContext(HeaderContext)
  const [isScrollAtTop, setIsScrollAtTop] = useState(true)
  const [isMobile, setIsMobile] = useState(false)
  const mobileBreakpoint = 768

  useEffect(() => {
    const handleWindowResize = () => setIsMobile(window.innerWidth < mobileBreakpoint)
    const handleWindowScrollThrottled = throttle(() => setIsScrollAtTop(window.pageYOffset === 0), 100)
    const handleWindowResizeThrottled = throttle(handleWindowResize, 100)

    handleWindowResize()
    window.addEventListener('scroll', handleWindowScrollThrottled)
    window.addEventListener('resize', handleWindowResizeThrottled)

    return () => {
      window.removeEventListener('scroll', handleWindowScrollThrottled)
      window.removeEventListener('resize', handleWindowResizeThrottled)
    }
  }, [])

  useEffect(() => {
    headerContext?.setIsClear(solid ? false : !isMobile && isScrollAtTop)
  }, [isScrollAtTop, isMobile])

  return null
}

/** Clear header until the masthead is scrolled past (lens outside the US). */
export function ClearHeaderOverMasthead() {
  const headerContext = useContext(HeaderContext)

  useEffect(() => {
    const handleWindowScrollThrottled = throttle(() => {
      headerContext?.setIsClear(window.pageYOffset < (window.innerWidth > 678 ? 280 : 200))
    }, 100)
    window.addEventListener('scroll', handleWindowScrollThrottled)

    return () => {
      window.removeEventListener('scroll', handleWindowScrollThrottled)
    }
  }, [])

  return null
}

/** Keep the header solid (US lens and procedure). */
export function SolidHeader() {
  const headerContext = useContext(HeaderContext)

  useEffect(() => {
    headerContext?.setIsClear(false)
  }, [headerContext?.isClear])

  return null
}

/** Sticky header with a reading progress bar (blog articles). */
export function ArticleHeader() {
  const headerContext = useContext(HeaderContext)

  useEffect(() => {
    headerContext?.setPosition('sticky')
    headerContext?.setHasProgressBar(true)

    return () => {
      headerContext?.setPosition('fixed')
      headerContext?.setHasProgressBar(false)
    }
  })

  return null
}

/** Reading progress bar (testimonials). */
export function ProgressBarHeader() {
  const headerContext = useContext(HeaderContext)

  useEffect(() => {
    headerContext?.setHasProgressBar(true)

    return () => {
      headerContext?.setHasProgressBar(false)
    }
  }, [])

  return null
}
